import apiClient from './api';
import { saveAuthToken, clearAuthData } from './lineService';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

/**
 * Login with username (ID card number) and password (birth date)
 * @param {string} username - ID card number (13 digits)
 * @param {string} password - Birth date in YYYY-MM-DD format
 * @returns {Promise} Response with token
 */
export const login = async (username, password, lineuid = '') => {
  try {
    console.log('🔑 Login attempt with lineuid:', { username, password, lineuid });

    const response = await apiClient.post('/Login.php', {
      username,
      password,
      lineuid
    });

    console.log(' Login response:', response);

    // Save token and user info using persistent storage if login successful
    if (response.token) {
      saveAuthToken(response.token, null);
      localStorage.setItem('username', username);

      // Save phone number if available
      if (response.tel || response.phone) {
        localStorage.setItem('userPhone', response.tel || response.phone);
      }
    }

    return response;
  } catch (error) {
    console.error('❌ Login error:', error.response?.data || error.message);
    throw error;
  }
};

/**
 * Send OTP to phone number
 * @param {string} tel - Phone number
 * @returns {Promise} Response
 */
export const sendOtp = async (tel) => {
  try {
    console.log('📱 Sending OTP to:', tel);

    const response = await apiClient.post('/OTPSend.php', {
      tel
    });

    console.log('✅ OTP Send response:', response);
    return response;
  } catch (error) {
    console.error('❌ OTP Send API Error');
    console.error('Status:', error.response?.status);
    console.error('Endpoint:', '/OTPSend.php');
    console.error('Message:', error.message);
    throw error;
  }
};

/**
 * Verify OTP
 * @param {string} tel - Phone number
 * @param {string} otp - OTP code
 * @param {string} ref - Reference code from OTP send
 * @returns {Promise} Response
 */
export const verifyOtp = async (tel, otp, ref) => {
  try {
    console.log('🔐 Verifying OTP:', otp, 'for phone:', tel, 'with ref:', ref);

    const response = await apiClient.post('/OTPVerify.php', {
      tel,
      otp,
      ref
    });

    console.log('✅ OTP Verify response:', response);
    return response;
  } catch (error) {
    console.error('❌ OTP Verify API Error');
    console.error('Status:', error.response?.status);
    console.error('Data:', error.response?.data);
    console.error('Endpoint:', '/OTPVerify.php');
    console.error('Message:', error.message);
    throw error;
  }
};

/**
 * Register new user
 * @param {Object} userData - User registration data from all 3 steps
 * @returns {Promise} Response
 */
export const register = async (userData) => {
  try {
    console.log('📝 Register userData:', userData);

    // Use birthdate directly (already in YYYY-MM-DD format)
    const birthday = userData.birthdate;

    // Map gender (male=1, female=0)
    const gender = userData.gender === 'male' ? '1' : '0';

    // Congenital disease list (already in array format)
    const congenitalDiseaseList = userData.congenital_disease || [];
    const congenitalDiseaseGrant = congenitalDiseaseList.length > 0 ? '1' : '0';

    // Map alcohol data
    let alcoholDrink = '';
    let alcoholDrinkStartAge = '';
    let alcoholDrinkAverage = '';
    let alcoholDrinkStopAge = '';

    if (userData.alcohol === 'ไม่เคยดื่ม') {
      alcoholDrink = '0';
    } else if (userData.alcohol === 'ดื่มเป็นบางโอกาส') {
      alcoholDrink = '1';
    } else if (userData.alcohol === 'ดื่มเป็นประจำ') {
      alcoholDrink = '2';
      alcoholDrinkStartAge = userData.alcohol_amount || '';
      alcoholDrinkAverage = userData.alcohol_frequency || '';
    } else if (userData.alcohol === 'หยุดดื่ม') {
      alcoholDrink = '3';
      alcoholDrinkStartAge = userData.alcohol_amount || '';
      alcoholDrinkAverage = userData.alcohol_frequency || '';
      alcoholDrinkStopAge = userData.alcohol_quit_age || '';
    }

    // Map smoking data
    let smoking = '';
    let smokingStartAge = '';
    let smokingStopAge = '';
    let smokingAverage = '';

    if (userData.smoking === 'ไม่เคยสูบ') {
      smoking = '0';
    } else if (userData.smoking === 'สูบบุหรี่') {
      smoking = '1';
      smokingStartAge = userData.smoking_amount || '';
      smokingAverage = userData.smoking_duration || '';
    } else if (userData.smoking === 'หยุดสูบบุหรี่') {
      smoking = '2';
      smokingStartAge = userData.smoking_amount || '';
      smokingStopAge = userData.smoking_duration || '';
      smokingAverage = userData.smoking_quit_amount || '';
    }

    const payload = {
      nid: userData.idCard,                // National ID (เลขบัตรประชาชน) - for login username
      tel: userData.phone,                 // Phone number
      name: userData.firstname,
      lastname: userData.lastname,
      gender: gender,
      birthday: birthday,
      height: userData.height,
      weight: userData.weight,
      waistline: userData.waist || '',
      congenitaldisease_grant: congenitalDiseaseGrant,
      congenitaldisease: congenitalDiseaseList.join(', '),
      alcohol_drink: alcoholDrink,
      alcohol_drink_start_age: alcoholDrinkStartAge,
      alcohol_drink_average: alcoholDrinkAverage,
      alcohol_drink_stop_age: alcoholDrinkStopAge,
      smoking: smoking,
      smoking_start_age: smokingStartAge,
      smoking_stop_age: smokingStopAge,
      smoking_average: smokingAverage,
      image: '',                           // Profile image (empty for now)
      lineuid: userData.lineuid || ''      // Line UID
    };

    console.log('Register payload:', payload);

    const response = await apiClient.post('/Register.php', payload);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Logout - clear all auth data
 */
export const logout = () => {
  clearAuthData();
  localStorage.removeItem('username');
};

/**
 * Check if user is logged in
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('userToken');
};

/**
 * Get current user token
 * @returns {string|null}
 */
export const getToken = () => {
  return localStorage.getItem('userToken');
};
